import { inject, injectable } from 'inversify';
import * as moment from 'moment';

import { HttpService } from 'src/core/services/http.service';
import { ObjectUtils } from 'src/core/utils/object';

/*
 * Response types
 */
declare global {
	interface IAgentCommissionResult {
		commissions: IQuoteEntryCommission[];
	}

	interface IQuoteEntryCommission {
		quoteEntryId: string;
		cost: ICommissionResult;
		sell: ICommissionResult;
	}

	interface ICommissionResult {
		commissionable: number;
		percent: number;
		commission: number;
	}
}

@injectable()
export class PolicyCheckingService {
	constructor(
		@inject(HttpService)
		private httpService: HttpService
	) { }

	private baseUrl = 'api/v1/quotes/policy';

	async getAgentCommission(quote: QuoteItem, entries: QuoteEntryItem[]): Promise<IAgentCommissionResult> {
		const body = {
			bookedProducts: (entries == null || entries.length == 0)
				? []
				: entries.map((entry) => this.mapToBookedProduct(entry)),
			agentId: quote.agentId,
			bookingDate: quote.reservationDate,
			brandId: quote.brandId,
			marketId: quote.marketId,
			regionId: quote.travelRegionId
		};

		const response = await this.httpService.post(`${this.baseUrl}/agent`, body);
		return response.data as IAgentCommissionResult;
	}

	async setCommissions(quote: QuoteItem, entries: QuoteEntryItem[]): Promise<void> {
		const baseEntries = entries.filter(m => m.productType !== 6);
		if (quote.agentId == null) {
			baseEntries.forEach((entry) => {
				entry.dynamicProperties = {
					...entry.dynamicProperties,
					costCommission: null,
					sellCommission: null
				};
			});
			quote.dynamicProperties = {
				...quote.dynamicProperties,
				costCommission: null,
				sellCommission: null
			};

			return;
		}

		const { commissions } = await this.getAgentCommission(quote, entries);

		baseEntries.forEach((entry) => {
			const commission = commissions
				.find(c => c.quoteEntryId == entry.id);
			entry.dynamicProperties = {
				...entry.dynamicProperties,
				costCommission: commission.cost,
				sellCommission: commission.sell
			};
		});

		const computeCommission = (prop, isCost: boolean = true) => {
			const commissionable = ObjectUtils.round(commissions.reduce((acc, curr) => {
				const quoteEntry = baseEntries.find(e => e.id === curr.quoteEntryId);
				if (isCost && quoteEntry.dynamicProperties.costPriceResult.currencyIsoCode !== quoteEntry.dynamicProperties.sellPriceResult.currencyIsoCode) {
					const commissionableInSellCurrency = curr[prop].commissionable * quoteEntry.dynamicProperties.sellPriceResult.exchangeRate;
					return acc + commissionableInSellCurrency;
				} else {
					return acc + curr[prop].commissionable;
				}
			}, 0));
			const commission = ObjectUtils.round(commissions.reduce((acc, curr) => {
				const quoteEntry = baseEntries.find(e => e.id === curr.quoteEntryId);
				if (isCost && quoteEntry.dynamicProperties.costPriceResult.currencyIsoCode !== quoteEntry.dynamicProperties.sellPriceResult.currencyIsoCode) {
					const commissionableInSellCurrency = curr[prop].commission * quoteEntry.dynamicProperties.sellPriceResult.exchangeRate;
					return acc + commissionableInSellCurrency;
				} else {
					return acc + curr[prop].commission;
				}
			}, 0));
			return {
				commission,
				commissionable,
				percent: commissionable > 0 ? ObjectUtils.round(commission / commissionable) : 0
			}
		}

		quote.dynamicProperties = {
			...quote.dynamicProperties,
			costCommission: computeCommission('cost'),
			sellCommission: computeCommission('sell', false)
		};
	}

	async getAdditionalProduct(quote: QuoteItem, entry: QuoteEntryItem, search: Search & SearchDates): Promise<ApiResult[]> {
		const body = {
			bookingDate: quote.reservationDate,
			brandId: quote.brandId,
			marketId: quote.marketId,
			regionId: quote.travelRegionId,
			productProviderApiName: entry.dynamicProperties.productProviderApiName,
			bookedProduct: this.mapToBookedProduct(entry),
			searchParameters: {
				bookingDate: quote.reservationDate,
				marketId: quote.marketId,
				startDate: search.startDate,
				endDate: search.endDate,
				units: search.occupancies
			}
		};

		const response = await this.httpService.post(`${this.baseUrl}/product`, body);
		return response.data;
	}

	async getFees(quote: QuoteItem, clients: QuotePaxItem[]): Promise<ApiResult[]> {
		const startDate = new Date(quote.reservationDate);
		const endDate = new Date(quote.reservationDate);
		endDate.setDate(endDate.getDate() + 1);
		const paxes = clients
			.map(m => ({
				id: m.id,
				name: `${m.dynamicProperties['salutation'] || ''} ${m.dynamicProperties['foreName'] || ''} ${m.dynamicProperties['middleName'] || ''} ${m.dynamicProperties['surName'] || ''}`.trim(),
				age: moment.utc(quote.reservationDate).diff(moment(m.dynamicProperties['birthDate']), 'year'),
			}));

		const body = {
			bookingDate: quote.reservationDate,
			brandId: quote.brandId,
			marketId: quote.marketId,
			regionId: quote.travelRegionId,
			searchParameters: {
				bookingDate: quote.reservationDate,
				marketId: quote.marketId,
				startDate: startDate,
				endDate: endDate,
				units: [{
					unit: 1, paxs: paxes
				}]
			}
		};

		const response = await this.httpService.post(`${this.baseUrl}/quote`, body);
		return response.data;
	}
	async getVisibilityProduct(quote: QuoteItem, entry: QuoteEntryItem, search: Search & SearchDates): Promise<VisibilityItem> {
		const body = {
			bookingDate: quote.reservationDate,
			brandId: quote.brandId,
			marketId: quote.marketId,
			regionId: quote.travelRegionId,
			productProviderApiName: entry.dynamicProperties.productProviderApiName,
			bookedProduct: this.mapToBookedProduct(entry),
			searchParameters: {
				bookingDate: quote.reservationDate,
				marketId: quote.marketId,
				startDate: search.startDate,
				endDate: search.endDate,
				units: search.occupancies
			}
		};

		const response = await this.httpService.post(`${this.baseUrl}/product-visibility`, body);
		return response.data;
	}
	private mapToBookedProduct(entry: QuoteEntryItem): any {
		return {
			productId: entry.productId,
			quoteEntryId: entry.id,
			parentEntryId: entry.parentEntryId,
			isSupplementOrDiscount: entry.productType === 6,
			costPrice: entry.dynamicProperties.costPriceResult,
			sellPrice: entry.dynamicProperties.sellPriceResult,
			supplierId: entry.supplierId,
			serviceFrom: entry.dateFrom,
			serviceTo: entry.dateTo,
			
		};
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/shared/policy-checking.service.ts